import React, { useCallback, useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { raffleApi } from './raffleApi';
import { Avatar, Notice, SectionLabel, Spinner } from './RafflePieces';
import { downloadNumbersStrip, printNumbersStrip } from './rafflePdf';
import { INDIGO, SLATE, formatPhone, fullName, money } from './raffleTokens';

const SEARCH_DEBOUNCE_MS = 400;

/**
 * "Their numbers" — spec §4.5, R94/R95.
 *
 * Look a customer up, see every auto raffle number they have earned this
 * cycle with the visit that earned it, and print the strip they take away.
 *
 * Open to anyone who can run a raffle, not just managers: handing a customer
 * their own numbers is counter work, and the screen shows nothing they could
 * not read off a strip they already hold.
 */
export default function NumbersView({ boot, locationId, notify }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const autoDrawings = (boot.drawings || []).filter((d) => d.auto);

  useEffect(() => () => clearTimeout(timer.current), []);

  const onQuery = (v) => {
    setQuery(v);
    clearTimeout(timer.current);
    if (v.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    timer.current = setTimeout(async () => {
      const res = await raffleApi.searchCustomers(locationId, v.trim());
      setSearching(false);
      setResults(res.success ? res.customers || [] : []);
    }, SEARCH_DEBOUNCE_MS);
  };

  const load = useCallback(
    async (customer) => {
      setLoading(true);
      const res = await raffleApi.customerNumbers(
        locationId,
        customer.customerId
      );
      setLoading(false);
      if (!res.success) {
        notify('error', res.message || 'Their numbers could not be loaded.');
        return;
      }
      setData(res);
    },
    [locationId, notify]
  );

  const open = (customer) => {
    setPicked(customer);
    setData(null);
    load(customer);
  };

  const back = () => {
    setPicked(null);
    setData(null);
  };

  const stripFor = (group) => ({
    locationName: data.locationName,
    customer: data.customer,
    group,
    printedBy: data.printedBy,
  });

  const onPrint = (group) => {
    try {
      printNumbersStrip(stripFor(group));
    } catch (e) {
      notify('error', 'The strip could not be prepared for printing.');
    }
  };

  const onDownload = (group) => {
    try {
      downloadNumbersStrip(stripFor(group));
    } catch (e) {
      notify('error', 'The strip could not be saved.');
    }
  };

  // A location with no auto drawing has nothing to look up — say why rather
  // than showing an empty search that can only ever find nothing.
  if (!autoDrawings.length) {
    return (
      <div className="rf-pad">
        <Notice Icon={ConfirmationNumberIcon}>
          No auto-number raffle is set up at this location.{' '}
          {boot.canManage
            ? 'Add one in Raffle Settings, with its numbers set to "NuVe auto numbers", and every qualifying visit starts earning a number.'
            : 'A manager can add one in Raffle Settings.'}
        </Notice>
      </div>
    );
  }

  if (!picked) {
    return (
      <div className="rf-pad rf-col rf-gap-16">
        <div className="rf-card">
          <div className="rf-search">
            <SearchIcon style={{ color: SLATE }} />
            <input
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder="Name, phone or customer ID"
              autoFocus
            />
            {searching && <Spinner size={16} color={INDIGO} />}
            {query && (
              <button
                type="button"
                className="rf-icon-btn"
                onClick={() => onQuery('')}
                aria-label="Clear"
              >
                <CloseIcon style={{ color: SLATE, fontSize: 18 }} />
              </button>
            )}
          </div>
          {!query && (
            <div className="rf-xs rf-muted" style={{ marginTop: 12 }}>
              Look up a customer to see the raffle numbers they have earned this
              cycle, and print their strip.
            </div>
          )}
        </div>

        <div className="rf-results-grid">
          {results.map((c) => (
            <button
              key={c.customerId}
              type="button"
              className="rf-card rf-pick"
              onClick={() => open(c)}
            >
              <Avatar name={fullName(c)} src={c.photoUrl} />
              <div className="rf-flex1 rf-minw0">
                <div className="rf-strong">{fullName(c)}</div>
                <div className="rf-xs rf-muted">{formatPhone(c.phone)}</div>
                <div className="rf-xs rf-muted">
                  Last visit: {c.lastVisit || '—'}
                </div>
              </div>
              <ChevronRightIcon style={{ color: SLATE }} />
            </button>
          ))}
          {query.trim().length >= 2 && !searching && results.length === 0 && (
            <div className="rf-card rf-empty">
              <div className="rf-strong">No customer matches “{query}”</div>
              <div className="rf-xs rf-muted" style={{ marginTop: 4 }}>
                Numbers are earned on a visit, so the customer has to be
                registered before they can hold any.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rf-pad rf-col rf-gap-16">
      <button
        type="button"
        className="rf-pill-btn rf-self-start"
        onClick={back}
      >
        <ArrowBackIcon style={{ fontSize: 16 }} /> Another customer
      </button>

      <div className="rf-card rf-row rf-gap-12 rf-items-center">
        <Avatar name={fullName(picked)} src={picked.photoUrl} size={48} />
        <div className="rf-flex1 rf-minw0">
          <div className="rf-h3">
            {data?.customer?.name || fullName(picked)}
          </div>
          <div className="rf-xs rf-muted">
            {formatPhone(data?.customer?.phone || picked.phone)}
          </div>
        </div>
        {data && (
          <span className="rf-badge-lav">
            <ConfirmationNumberIcon style={{ fontSize: 13 }} /> {data.total}
          </span>
        )}
      </div>

      {loading && (
        <div className="rf-center" style={{ minHeight: 120 }}>
          <Spinner color={SLATE} size={26} />
        </div>
      )}

      {data &&
        !loading &&
        data.drawings.map((group) => (
          <div key={group.drawingId} className="rf-card rf-col rf-gap-12">
            <div className="rf-row rf-gap-8 rf-items-center">
              <div className="rf-flex1 rf-minw0">
                <div className="rf-strong">{group.drawingName}</div>
                <div className="rf-xs rf-muted">
                  {group.cycle?.lastIssued
                    ? `Cycle ${group.cycle.autoFrom}–${group.cycle.lastIssued} · ${group.cycle.issuedCount} issued`
                    : 'No numbers issued in this cycle yet'}
                  {group.rollover && group.pot
                    ? ` · Pot ${money(group.pot)}`
                    : ''}
                </div>
              </div>
              <span className={group.count ? 'rf-badge-lav' : 'rf-badge-muted'}>
                {group.count}
              </span>
            </div>

            {/* The rollover was won, so this cycle is over. The numbers stay
                on screen — they are the record of the drawing that just
                happened — but they are no longer live. */}
            {group.seriesClosed && (
              <Notice tone="amber">
                This drawing was won. These numbers were in the cycle that
                ended; a new cycle starts once it is opened again.
              </Notice>
            )}

            {group.count === 0 ? (
              <div className="rf-xs rf-muted">
                No numbers yet this cycle. The next qualifying visit earns one.
              </div>
            ) : (
              <>
                <SectionLabel>Numbers earned</SectionLabel>
                <div className="rf-numbers-list">
                  {group.numbers.map((n) => (
                    <div key={n.entryId} className="rf-number-row">
                      <span className="rf-number-no">#{n.ticketNo}</span>
                      <span className="rf-number-when">
                        {n.date}, {n.time}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="rf-row rf-gap-8">
              <button
                type="button"
                className="rf-btn rf-btn-navy rf-flex1"
                disabled={!group.count}
                onClick={() => onPrint(group)}
              >
                <PrintIcon style={{ fontSize: 17 }} /> Print strip
              </button>
              <button
                type="button"
                className="rf-btn rf-btn-outline"
                disabled={!group.count}
                onClick={() => onDownload(group)}
              >
                <DownloadIcon style={{ fontSize: 17 }} /> Save
              </button>
            </div>
          </div>
        ))}
    </div>
  );
}
